import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseTable from '../shared/table/BaseTable';
import { useColumns } from './hooks/useColumns';
import DocumentToolbar from './DocumentToolbar';
import DocumentBulkActions from './DocumentBulkActions';
import DocumentCard from './DocumentCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const DocumentList = ({
  documents = [],
  selectedDocuments = new Set(),
  setSelectedDocuments,
  density = 'standard',
  onUploadClick,
  onRefresh
}) => {
  const columns = useColumns(documents, density);
  
  const navigate = useNavigate();
  
  const handleRowClick = useCallback((id, button) => {
    const document = documents.find(doc => doc.id === id);
    if (!document) return;
    
    // Middle click opens in new tab, left click navigates in current tab
    if (button === 1) { // Middle click
      window.open(`/documents/${id}`, '_blank');
    } else { // Left click
      navigate(`/documents/${id}`);
    }
  }, [documents, navigate]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleSelectDocument = (docId) => {
    const newSelection = new Set(selectedDocuments);
    if (newSelection.has(docId)) {
      newSelection.delete(docId);
    } else {
      newSelection.add(docId);
    }
    setSelectedDocuments(newSelection);
  };

  const memoizedTable = useMemo(() => (
    isMobile ? (
      <div className="p-4 space-y-4 bg-gray-50 overflow-y-auto">
        {documents.map(doc => (
          <DocumentCard
            key={doc.id}
            document={doc}
            onRowClick={handleRowClick}
            isSelected={selectedDocuments.has(doc.id)}
            onSelect={handleSelectDocument}
          />
        ))}
      </div>
    ) : (
      <BaseTable
        data={documents}
        columns={columns}
        selectedItems={selectedDocuments}
        onSelectionChange={setSelectedDocuments}
        onRowClick={handleRowClick}
        density={density}
        renderCustomRow={null} // Use default row rendering
        showThumbnails={false}
      />
    )
  ), [
    documents,
    columns,
    selectedDocuments,
    setSelectedDocuments,
    handleRowClick,
    density,
    isMobile
  ]);

  const memoizedBulkActions = useMemo(() => (
    <DocumentBulkActions
      selectedDocuments={selectedDocuments}
      setSelectedDocuments={setSelectedDocuments}
      onUpdate={onRefresh}
      documentList={documents}
    />
  ), [documents, selectedDocuments, setSelectedDocuments, onRefresh]);

  return (
    <div className="flex flex-col h-full">
        <DocumentToolbar
          title="Documents"
          columns={columns}
          data={documents}
          showQuickFilter={true}
          quickFilterProps={{ debounceMs: 300 }}
          onUploadClick={onUploadClick}
        />
        <div className="flex-1 min-h-0">
          {memoizedTable}
        </div>
        {memoizedBulkActions}
    </div>
  );
};

export default React.memo(DocumentList);
